// @LTG-ID: data/journals/increase-business-value-before-selling
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0107
// @CHANGED: 2026-07-06 (CHG-0107 · Journals content port from batch MD)
// @OWNER: web
//
// Article 20 of 20. Source: batch MD at /app/_ltg_analysis/content_batches/.
// Byline: LTG Team (per CHG-0107 orchestrator Decision 2 / batch preamble directive).

export const BRIEF = {
    SLUG: "increase-business-value-before-selling",
    FRONTMATTER: {
        number: "57",
        section: "Value Drivers",
        sectionHref: "/journals#value-drivers",
        sectionAnchor: "value-drivers",
        title: "How to Increase the Value of Your Business Before Selling: A 24-Month Checklist",
        deck: "To increase business value before selling, spend 18 to 24 months reducing owner dependence, building recurring revenue, diversifying customers, cleaning financial records, deepening the management team, and documenting processes.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-05-01",
        publishedHuman: "May 1, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 3,
        wordCount: 495,
        timeRequired: "PT3M",
        keywords: "how to increase business value before selling, prepare business for sale checklist, increase company value, business sale preparation",
        articleSection: "Value Drivers",
        articleBodyPreview: "To increase business value before selling, spend 18 to 24 months reducing owner dependence, building recurring revenue, diversifying customers, cleaning financial records, deepening the management team, and documenting processes. These value drivers determine where a business prices within its industry range and how favorable the deal terms are. Value building is not a mystery; it is a work list. This checklist compresses the whole series into a 24 month program an owner can actually run. Every ",
        complianceNote: "This article is educational and does not constitute a valuation engagement or financial, legal, or tax advice.",
        needsDisclaimerFooter: false,
        internalLinks: [
        "exit-planning-guide",
        "owner-dependence-business-value",
        "business-value-drivers",
        "recurring-revenue-business-value",
        "customer-concentration-selling-business",
        "clean-financial-records-business-value",
        "management-team-business-value",
        "business-systems-processes-value",
        "does-growth-increase-business-value",
        "add-backs-selling-a-business",
        "due-diligence-selling-a-business"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "How to Increase the Value of Your Business Before Selling: A 24-Month Checklist",
        canonical: "https://letstalkgrowth.com/journals/increase-business-value-before-selling",
        ogImage: "/journals/og/increase-business-value-before-selling.jpg",
    },
    BODY_MARKDOWN: `To increase business value before selling, spend 18 to 24 months reducing owner dependence, building recurring revenue, diversifying customers, cleaning financial records, deepening the management team, and documenting processes. These value drivers determine where a business prices within its industry range and how favorable the deal terms are.

Value building is not a mystery; it is a work list. This checklist compresses the whole series into a 24-month program an owner can actually run. Every item links to a full article in this Journal for depth.

## Months 24 to 18: Establish the baseline

- Get a market-based estimate of what the business is worth today, and understand which factors are holding it below the top of its range.
- Engage your CPA to move the books onto a consistent, reconciled footing. The trailing record buyers will evaluate is being written now.
- Start the add-back discipline: document every legitimate adjustment as it occurs instead of reconstructing it later.
- Write down your personal number: what the sale must produce, after taxes and debts, for your next chapter to work. Every subsequent decision gets easier with this number known.

## Months 18 to 12: Attack the two biggest discounts

- Begin the owner-independence program: document what only you know, delegate real decision authority, and introduce second contacts into every key customer relationship.
- Address customer concentration if any account exceeds the caution thresholds: grow around the anchor, and put assignable contracts on informal relationships.
- Launch or expand recurring revenue: service agreements, memberships, maintenance plans, whatever repeatable offer fits your industry.
- Identify and start developing your second-in-command.

## Months 12 to 6: Make it provable

- Take the vacation test: a genuine multi-week absence with the phone quiet. Fix whatever breaks; that list is your remaining dependence.
- Complete the core process documentation: price book, delivery SOPs, onboarding path, customer records in a system the business owns.
- Review every key contract and lease for assignability with your attorney, and resolve consent issues before a buyer finds them.
- Deal with the known skeletons now: tax notices, license gaps, equipment issues, and anything else you would rather disclose than have discovered.

## Months 6 to 0: Prepare the transaction itself

- Assemble the data room: the standard diligence document set, organized and pre-reviewed by your own CPA and attorney.
- Prepare the retention plan for key employees, including stay bonuses where appropriate.
- Keep performance up. Buyers pay for trailing results and momentum, and the sale process itself is the classic distraction that erodes both.
- Choose your deal team and go to market from strength, on your timeline, not a trigger's.

## The honest summary

None of these items is exotic, and that is the point. The market's data is consistent: most businesses that list never sell, and the failures trace to a short list of preventable causes. The owners who capture full value are simply the ones who did this unglamorous work while there was still time for it to show up in the numbers.`,
    FAQ: [
        { question: "How long before selling should I start preparing my business?", answer: "Eighteen to twenty-four months is a practical minimum for the improvements buyers pay for to appear in trailing results. Three or more years is better, particularly for reducing owner dependence and building recurring revenue." },
        { question: "What increases business value the fastest?", answer: "Cleaning financial records and documenting defensible add-backs typically show results fastest, because they make existing earnings believable. Structural drivers like owner independence and recurring revenue take longer but move value further." },
        { question: "Is it worth improving the business if I want to sell immediately?", answer: "Even a compressed version of this list, organized records, a prepared data room, and resolved legal housekeeping, improves price, terms, and the odds of closing. But sellers forced to market unprepared should calibrate expectations accordingly." }
    ],
    SOURCES: [
        { id: "s1", html: "Exit Planning Institute, <em>State of Owner Readiness</em> research — value-driver and transferability frameworks." },
        { id: "s2", html: "BizBuySell, <em>Insight Reports</em> — buyer preference and pricing data." },
        { id: "s3", html: "International Business Brokers Association (IBBA), <em>Market Pulse</em> survey." }
    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal →",
        microLine: "· 7-DAY DELIVERY · KGOB METHODOLOGY, NC CPA #30420 ·",
    },
    READ_NEXT: [
        {
            tag: "Exit Planning",
            title: "Exit Planning 101: How to Leave Your Business on Your Own Terms",
            meta: "4 min · Brief 39",
            to: "/journals/exit-planning-guide",
        },
        {
            tag: "Value Drivers",
            title: "Owner Dependence: The Biggest Hidden Discount on Your Business's Value",
            meta: "3 min · Brief 45",
            to: "/journals/owner-dependence-business-value",
        },
        {
            tag: "Value Drivers",
            title: "The Value Drivers That Determine What Buyers Will Pay for Your Business",
            meta: "4 min · Brief 48",
            to: "/journals/business-value-drivers",
        },
    ],
};

export default BRIEF;
